import { useEffect, useMemo, useState, useRef } from "react";

import { apiRequest, ensureCsrfToken, getLegacyUrl } from "../../api";

import InvoiceCheckDetailModal from "../../components/InvoiceCheckDetailModal";

import ProcessorInvoiceEditModal from "../../components/ProcessorInvoiceEditModal";

import Swal from "sweetalert2";

import CheckerViewShell from "./CheckerViewShell";

import { openInvoiceData } from "../../utils/invoiceDisplay";
import { openInvoicePdf } from "../../utils/openPdf";

function formatAmount(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(numericValue);
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
}

function getApprovalRows(approvalData) {
  const fieldRows = Object.entries(approvalData?.approval_req_field || {}).map(([parameter, row]) => ({
    section: "Field Matrix",
    parameter,
    sub: "",
    original: row?.Original_Value,
    updated: row?.Updated_Value,
    status: row?.Checker_Approavl || "pending",
  }));

  const radioRows = Object.entries(approvalData?.approval_req_radio || {}).map(([parameter, row]) => ({
    section: "Radio Matrix",
    parameter,
    sub: row?.Param || row?.param || row?.Section || "",
    original: row?.Original_Value,
    updated: row?.Updated_Value,
    status: row?.Checker_Approavl || "pending",
  }));

  return [...fieldRows, ...radioRows];
}

function hasPendingApprovals(approvalData) {
  return getApprovalRows(approvalData).some((row) => String(row.status).toLowerCase() === "pending");
}

function StatusModal({ detail, onClose }) {
  if (!detail) {
    return null;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checker-status-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Status Detail</p>
            <h2 id="checker-status-title">{detail.label}</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <p className="config-note">{detail.message || "No additional message available."}</p>

        <pre className="processor-modal__code">
          {JSON.stringify(detail.data ?? {}, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function TrailModal({ trail, loading, onClose }) {
  useEffect(() => {
    if (!trail && !loading) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [trail, loading, onClose]);

  if (!trail && !loading) {
    return null;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card processor-modal__card--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="processor-trail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Audit Log</p>
            <h2 id="processor-trail-title">Invoice timeline</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <p className="config-note">Loading audit log…</p>
        ) : trail?.length ? (
          <div className="processor-timeline">
            {trail.map((entry) => (
              <article key={`${entry.step}-${entry.created_at}`} className="processor-timeline__item">
                <div className="processor-timeline__step">Step {entry.step}</div>
                <div className="processor-timeline__content">
                  <strong>{entry.action}</strong>
                  <p>{entry.message || "No message recorded."}</p>
                  <span>
                    {(entry.username || "System")}
                    {entry.userrole ? ` (${entry.userrole})` : ""}
                    {entry.created_at ? ` • ${new Date(entry.created_at).toLocaleString()}` : ""}
                  </span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="empty-state">No timeline available for this invoice.</p>
        )}
      </div>
    </div>
  );
}

function ApprovalModal({ row, loadingKey, onApprove, onClose }) {
  if (!row) {
    return null;
  }

  const approvalRows = getApprovalRows(row.checkerApproval);

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card processor-modal__card--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checker-approval-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Checker Approval</p>
            <h2 id="checker-approval-title">Pending edits for invoice {row.invoiceId}</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        {approvalRows.length ? (
          <div className="config-table processor-table">
            <table>
              <thead>
                <tr>
                  <th>Section</th>
                  <th>Parameter</th>
                  <th>Original</th>
                  <th>Updated</th>
                  <th>Status</th>
                  <th>Approve</th>
                </tr>
              </thead>
              <tbody>
                {approvalRows.map((approvalRow) => {
                  const actionId = `${row.invoiceGroupKey}-${approvalRow.section}-${approvalRow.parameter}-${approvalRow.sub || "none"}`;
                  const isApproved = String(approvalRow.status).toLowerCase() !== "pending";

                  return (
                    <tr key={actionId}>
                      <td>{approvalRow.section}</td>
                      <td>{approvalRow.sub ? `${approvalRow.parameter} -> ${approvalRow.sub}` : approvalRow.parameter}</td>
                      <td>{approvalRow.original || "-"}</td>
                      <td>{approvalRow.updated || "-"}</td>
                      <td>{isApproved ? "Approved" : "Pending"}</td>
                      <td>
                        <button
                          className="ghost-button"
                          type="button"
                          disabled={isApproved || loadingKey === actionId}
                          onClick={() =>
                            onApprove(row, {
                              matrix: approvalRow.section,
                              parameter: approvalRow.parameter,
                              sub: approvalRow.sub || "",
                              type: "single",
                              actionId,
                            })
                          }
                        >
                          {loadingKey === actionId ? "Approving..." : isApproved ? "Approved" : "Approve"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    <button
                      className="primary-button"
                      type="button"
                      disabled={loadingKey === `${row.invoiceGroupKey}-all`}
                      onClick={() =>
                        onApprove(row, {
                          matrix: "all",
                          parameter: "all",
                          sub: "all",
                          type: "all",
                          actionId: `${row.invoiceGroupKey}-all`,
                        })
                      }
                    >
                      {loadingKey === `${row.invoiceGroupKey}-all` ? "Approving..." : "Approve All Changes"}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">No checker approval items are attached to this invoice.</p>
        )}
      </div>
    </div>
  );
}

export default function CheckerPendingView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState("");
  const [approvalLoadingKey, setApprovalLoadingKey] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [statusDetail, setStatusDetail] = useState(null);
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);
  const [approvalRow, setApprovalRow] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [statusInvoiceRow, setStatusInvoiceRow] = useState(null);
  const [filteredRows, setFilteredRows] = useState([]);
  const [teams, setTeams] = useState([]);
  const [userTeam, setUserTeam] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [scoringOptions, setScoringOptions] = useState({});
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;

  const summary = useMemo(() => {
    const pendingApprovalCount = rows.filter((row) => hasPendingApprovals(row.checkerApproval)).length;

    return {
      count: rows.length,
      selected: selectedRows.length,
      redFlags: rows.reduce(
        (count, row) =>
          count + Object.values(row.invoiceCheck || {}).filter((item) => item?.color === "r").length,
        0,
      ),
      pendingApprovalCount,
    };
  }, [rows, selectedRows]);

  const menuRef = useRef(null);
  
  useEffect(() => {
    if (!menuPosition) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuPosition(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuPosition]);

  async function loadRows() {
    setLoading(true);
    setError("");

    try {
      const response = await apiRequest("/checker/pending-approvals/");

      const nextRows = response.data || [];
      const nextTeams = response.teams || [];
      const nextUserTeam = response.user_team || "";

      setRows(nextRows);
      setScoringOptions(response.scoringOptions || {});
      setTeams(nextTeams);
      setUserTeam(nextUserTeam);

      // Default filter = logged-in user's team
      setSelectedTeam(nextUserTeam);

      // Initially show only user's team's rows
      const userRows = nextRows.filter(
        (row) =>
          (row.team || "").trim().toLowerCase() ===
          nextUserTeam.trim().toLowerCase()
      );

      setFilteredRows(userRows);

      return nextRows;
    } catch (requestError) {
      setError(
        requestError.message ||
          "Unable to load checker pending approvals."
      );
      return [];
    } finally {
      setLoading(false);
    }
  }



  useEffect(() => {
    loadRows();
  }, []);

  function toggleRowSelection(invoiceGroupKey) {
    setSelectedRows((current) =>
      current.includes(invoiceGroupKey)
        ? current.filter((item) => item !== invoiceGroupKey)
        : [...current, invoiceGroupKey],
    );
  }

  function toggleAllRows() {
    setSelectedRows((current) => (current.length === rows.length ? [] : rows.map((row) => row.invoiceGroupKey)));
  }

  async function handleTrailOpen(row) {
    setTrail(null);
    setTrailLoading(true);

    try {
      const response = await fetch(`/api/invoice/trail/${row.invoiceId}/`, {
        credentials: "include",
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Unable to load trail.");
      }
      setTrail(payload.data || []);
    } catch (requestError) {
      setTrail([]);
      setError(requestError.message || "Unable to load trail.");
    } finally {
      setTrailLoading(false);
    }
  }

  

  

  async function handleAction(row, type) {
    setStatus("");
    setError("");

    if (type === "approve" && hasPendingApprovals(row.checkerApproval)) {
      await Swal.fire({
        icon: "warning",
        title: "Approval Pending",
        text: "Some changes need approval under the Approval tab. Please confirm all changes before approving the invoice.",
        confirmButtonText: "OK",
      });

      return;
    }

    setActionKey(`${type}-${row.invoiceGroupKey}`);

    try {
      const csrfToken = await ensureCsrfToken();
      let url = "";
      let body = {};

      if (type === "approve") {
        url = "/api/approve-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab:'Checker' };
      } else if (type === "hold") {
        url = "/api/hold-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab:'Checker' };
      } else if (type === "reject") {
        url = "/api/reject-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab:'Checker' };
      }

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(body),
      });

      const payload = await response.json();

    if (!response.ok || payload.status !== "success") {
      throw new Error(payload.message || `Unable to ${type} invoice.`);
    }

    await Swal.fire({
      icon: "success",
      title: `${type.charAt(0).toUpperCase() + type.slice(1)} Successful`,
      text: payload.message || `Invoice ${type}d successfully.`,
      confirmButtonText: "OK",
    });

    setSelectedRows((current) =>
      current.filter((item) => item !== row.invoiceGroupKey)
    );

    await loadRows();
    } catch (requestError) {
      await Swal.fire({
        icon: "error",
        title: `${type.charAt(0).toUpperCase() + type.slice(1)} Failed`,
        text: requestError.message || `Unable to ${type} invoice.`,
        confirmButtonText: "OK",
      });
    } finally {
      setActionKey("");
    }
  }

  async function handleApprovalAction(row, approvalAction) {
    setApprovalLoadingKey(approvalAction.actionId);

    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/checker-edits-approve/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          invoice_group_key: row.invoiceGroupKey,
          matrix: approvalAction.matrix,
          parameter: approvalAction.parameter,
          sub: approvalAction.sub,
          type: approvalAction.type,
        }),
      });

      const payload = await response.json();

      if (!response.ok || (payload.status !== "ok" && payload.status !== "success")) {
        throw new Error(payload.message || "Unable to approve checker edits.");
      }

      await Swal.fire({
        icon: "success",
        title: "Approval Updated",
        text: payload.message || "Checker approval updated successfully.",
        confirmButtonText: "OK",
      });

      const nextRows = await loadRows();

      setApprovalRow((current) => {
        if (!current) return null;

        return (
          nextRows.find(
            (item) => item.invoiceGroupKey === current.invoiceGroupKey
          ) || null
        );
      });
    } catch (requestError) {
      await Swal.fire({
        icon: "error",
        title: "Approval Failed",
        text: requestError.message || "Unable to approve checker edits.",
        confirmButtonText: "OK",
      });
    } finally {
      setApprovalLoadingKey("");
    }
  }

  async function handlePreview(row) {
    setStatus("");
    setError("");
    setActionKey(`preview-${row.invoiceGroupKey}`);
    
    // Open immediately from user click
    const previewWindow = window.open("", "_blank");

    if (!previewWindow) {
      setError("Preview window was blocked by the browser.");
      return;
    }

    previewWindow.document.write(
      "<html><body><h3>Loading preview...</h3></body></html>"
    );

    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/template-preview/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          invoice_keys: [row.invoiceGroupKey],
          material_service: row.material_service,
        }),
      });

      const payload = await response.json();

      if (!response.ok || payload?.status !== "success") {
        throw new Error(payload?.message || "Unable to generate preview.");
      }

      let html =
        "<html><head><title>Preview</title></head><body style='padding:24px;font-family:Segoe UI,sans-serif;'>";

      Object.entries(payload.template_html || {}).forEach(([key, value]) => {
        html += `<h3>${key}</h3>${value}<hr/>`;
      });

      html += "</body></html>";

      previewWindow.document.open();
      previewWindow.document.write(html);
      previewWindow.document.close();

    } catch (requestError) {
      previewWindow.close();
      setError(requestError.message || "Unable to generate preview.");
    } finally {
      setActionKey("");
    }
  }
  

  function refreshPendingActions() {
    loadRows();
  }

  return (
    <CheckerViewShell
      title="Checker Pending Approval"
      intro="Review invoices waiting with checker approval, inspect change approvals"
      metrics={[
        { label: "Queue", value: summary.count },
        
      ]}
      actions={
        <button
          className="ghost-button processor-view__refresh"
          type="button"
          onClick={refreshPendingActions}
        >
          Refresh Pending Actions
        </button>
      }
    >
      <section className="processor-view">

        {status ? <div className="config-banner config-banner--success">{status}</div> : null}
        {error ? <div className="config-banner config-banner--error">{error}</div> : null}

        <div className="workspace-panel">
          <div className="team-filter">
            <label htmlFor="team-filter">
              Filter by Team
            </label>

            <select
              id="team-filter"
              value={selectedTeam}
              onChange={(event) => {
                const team = event.target.value;

                setSelectedTeam(team);

                setFilteredRows(
                  rows.filter(
                    (row) =>
                      (row.team || "").trim().toLowerCase() ===
                      team.trim().toLowerCase()
                  )
                );
              }}
            >
              {teams.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </select>
          </div>
          {loading ? (
            <p className="config-note">Loading checker approvals…</p>
          ) : (
            <div className="config-table processor-table">
              <table>
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th>Vendor Name</th>
                    <th>Invoice No.</th>
                    <th>Invoice Date</th>
                    <th>Invoice Value</th>
                    <th>Status</th>
                    <th>Response</th>
                    <th>File</th>
                    <th>Preview</th>
                    <th>Approval</th>
                    <th>
                      Select
                      <input
                        className="processor-checkbox"
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAllRows}
                      />
                    </th>
                    <th>Logs</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length ? (
                    filteredRows.map((row) => (
                      <tr key={row.invoiceGroupKey}>
                        <td>{row.invoiceId}</td>
                        <td>{row.vendorName || "-"}</td>
                        <td>{row.invoiceNo || "-"}</td>
                        <td>{formatDate(row.invoiceDate)}</td>
                        <td>{formatAmount(row.invoiceValue)}</td>
                        <td>
                          <div className="processor-status-list">
                            {Object.entries(row.invoiceCheck || {}).length ? (
                              Object.entries(row.invoiceCheck || {}).map(([label, item]) => (
                                <button
                                  key={`${row.invoiceGroupKey}-${label}`}
                                  className={`processor-status-dot processor-status-dot--${item?.color || "o"}`}
                                  type="button"
                                  title={`${label}: ${item?.message || ""}`}
                                  onClick={() => {
                                  
                                    setStatusInvoiceRow(row);

                                    setStatusDetail({
                                      label,
                                      message: item?.message,
                                      data: item?.data,
                                      key: item?.key || label,
                                      checkKey: label,
                                      uniqueKey: row.invoiceGroupKey,
                                      color: item?.color || "o",
                                    });
                                  }}
                                />
                              ))
                            ) : (
                              <span className="empty-state">No checks</span>
                            )}
                          </div>
                        </td>
                        <td>
                          {row.uniqueName ? (
                            <button
                              onClick={() =>
                                openInvoiceData(
                                  `${row.uniqueName}.json`
                                )
                              }
                            >
                              Data
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          {row.uniqueName ? (
                            <button
                              type="button"
                              onClick={() => openInvoicePdf(row.invoiceId)}
                            >
                              📎
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <button
                            className="ghost-button"
                            type="button"
                            disabled={actionKey === `preview-${row.InvoiceGroupKey}`}
                            onClick={() => handlePreview(row)}
                          >
                            {actionKey === `preview-${row.InvoiceGroupKey}` ? "Previewing..." : "Preview"}
                          </button>
                        </td>
                        <td>
                          <button className="ghost-button" type="button" onClick={() => setApprovalRow(row)}>
                            {hasPendingApprovals(row.checkerApproval) ? "Approval Pending" : "Approved"}
                          </button>
                        </td>
                        <td>
                          <input
                            className="processor-checkbox"
                            type="checkbox"
                            checked={selectedRows.includes(row.invoiceGroupKey)}
                            onChange={() => toggleRowSelection(row.invoiceGroupKey)}
                          />
                        </td>
                        <td>
                          <button className="ghost-button" type="button" onClick={() => handleTrailOpen(row)}>
                            Logs
                          </button>
                        </td>
                        <td>
                          <div className="action-dropdown-wrapper">
                            <button
                              type="button"
                              className="action-menu-button"
                              onClick={(e) => {

                                if (
                                  menuPosition?.row?.invoiceGroupKey === row.invoiceGroupKey
                                ) {
                                  setMenuPosition(null);
                                  return;
                                }

                                const rect = e.currentTarget.getBoundingClientRect();

                                setMenuPosition({
                                  row,
                                  top: rect.bottom + window.scrollY,
                                  left: rect.left + window.scrollX,
                                });
                              }}
                            >
                              Actions ▼
                            </button>                         
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="13">No checker pending approvals found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {menuPosition && (
          <div
            ref={menuRef}
            className="global-action-menu"
            style={{
              position: "fixed",
              top: menuPosition.top,
              left: menuPosition.left,
              zIndex: 999999,
            }}
          >
            <button
              type="button"
              onClick={() => {
                handleAction(menuPosition.row, "approve");
                setMenuPosition(null);
              }}
            >
              ✓ Approve
            </button>

            <button
              type="button"
              onClick={() => {
                handleAction(menuPosition.row, "hold");
                setMenuPosition(null);
              }}
            >
              ⏸ Hold
            </button>

            <button
              type="button"
              onClick={() => {
                handleAction(menuPosition.row, "reject");
                setMenuPosition(null);
              }}
            >
              ✕ Reject
            </button>

            <button
              type="button"
              onClick={() => {
                setEditTarget({
                  invoiceGroupKey: menuPosition.row.invoiceGroupKey,
                });
                setMenuPosition(null);
              }}
            >
              ✎ Edit
            </button>

          </div>
        )}

        
        <InvoiceCheckDetailModal
          detail={statusDetail}
          invoiceRow={statusInvoiceRow}
          scoringOptions={scoringOptions}
          onClose={() => {
            setStatusDetail(null);
            setStatusInvoiceRow(null);
          }}
          onSaved={loadRows}
        />
        <TrailModal trail={trail} loading={trailLoading} onClose={() => setTrail(null)} />
        <ApprovalModal
          row={approvalRow}
          loadingKey={approvalLoadingKey}
          onApprove={handleApprovalAction}
          onClose={() => setApprovalRow(null)}
        />

        <ProcessorInvoiceEditModal
          invoiceGroupKey={editTarget?.invoiceGroupKey}
          onClose={() => setEditTarget(null)}
          onSaved={loadRows}
        />
      </section>
    </CheckerViewShell>
  );
}
